import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, Cpu } from "lucide-react";
import { Wordmark } from "@/components/Brand";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { label: "Features", href: "#features" },
  { label: "Why ClydeFlow", href: "#why" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const commandHref =
    user?.role === "admin" ? "/admin" : user ? "/dashboard" : null;

  const handleCommandCenter = () => {
    if (commandHref) {
      navigate(commandHref);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050B14]/70 border-b border-white/10"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="flex items-center gap-2 shrink-0"
        >
          <Wordmark className="h-14" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-slate-300 hover:text-white transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* === Command Hub (Status Bar) === */}
        <div
          className="hidden md:flex items-center gap-3"
          data-testid="command-hub"
        >
          <ThemeToggle />

          {/* Network Pulse */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 bg-[#0A192F]/80 border border-white/10 rounded-sm"
            data-testid="command-hub-network-pulse"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400 shadow-[0_0_8px_rgba(0,240,255,0.9)]" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
              Network Live
            </span>
            <span className="font-mono text-[10px] text-cyan-300 font-bold">
              250k+ Reach
            </span>
          </div>

          {/* Meta Verified badge */}
          <div
            className="hidden xl:flex items-center gap-2 px-3 py-1.5 bg-[#0A192F]/80 border border-white/10 rounded-sm"
            data-testid="command-hub-meta-verified"
            title="Official Meta Verified Tech Provider"
          >
            <ShieldCheck size={13} className="text-cyan-300" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
              Meta Verified
            </span>
          </div>

          {/* Google Preferred Sources — boosts visibility in Search & AI Overviews */}
          <div
            google-add-preferred-source-btn=""
            data-testid="google-preferred-source-header"
            className="flex items-center"
          />

          {/* Power Button */}
          <button
            data-testid="command-hub-cta"
            onClick={handleCommandCenter}
            className="group relative inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A192F] border border-cyan-400/50 text-white font-bold text-xs uppercase tracking-[0.18em] rounded-sm transition-all hover:bg-[#00F0FF] hover:text-[#050B14] hover:border-[#00F0FF]"
            style={{ boxShadow: "0 0 20px rgba(0, 240, 255, 0.25), inset 0 0 0 1px rgba(0,240,255,0.15)" }}
          >
            <Cpu size={14} className="text-cyan-300 group-hover:text-[#050B14]" />
            {user ? "Open Command Center" : "Start Your Empire Free"}
          </button>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-white/10 bg-[#050B14]/95"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-base text-slate-200 hover:text-white"
              >
                {item.label}
              </a>
            ))}
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400 font-mono uppercase tracking-widest">
              <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
              Network Live · 250k+ Reach
            </div>
            <button
              data-testid="nav-mobile-command-cta"
              onClick={handleCommandCenter}
              className="mt-2 px-4 py-3 text-sm font-bold bg-[#00F0FF] text-[#050B14] uppercase tracking-widest"
            >
              Open Command Center
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
